import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen, FileText, Upload, Settings, AlertCircle, Stethoscope } from 'lucide-react';
import { OverviewSection } from './help/OverviewSection';
import { ClaimsSection } from './help/ClaimsSection';
import { AdminSection } from './help/AdminSection';
import { BulkUploadSection } from './help/BulkUploadSection';
import { TroubleshootingSection } from './help/TroubleshootingSection';
import { WorkflowGuideSection } from './help/WorkflowGuideSection';
import { roleLabel } from './admin/adminShared';

const CLINICAL_ROLES = ['Consultant', 'Gynaecologist', 'Specialist', 'Pharmacist', 'Lab', 'Radiology'];

const Help = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const hospitalName = localStorage.getItem('hospital_name');
  const role = localStorage.getItem('role');
  const username = localStorage.getItem('username');

  const isSuperAdmin = username === 'superadmin' || (role === 'Admin' && hospitalName === 'System Administration');
  const isClinical = CLINICAL_ROLES.includes(role) || role === 'Reception';

  const sections = {
    ...(isClinical ? {} : {
      overview: { title: 'Getting Started', icon: BookOpen, render: () => <OverviewSection isSuperAdmin={isSuperAdmin} hospitalName={hospitalName} /> },
      claims: { title: 'Managing Claims', icon: FileText, render: () => <ClaimsSection isSuperAdmin={isSuperAdmin} /> },
    }),
    workflow: { title: 'Patient Workflow', icon: Stethoscope, render: () => <WorkflowGuideSection username={username} role={role} isSuperAdmin={isSuperAdmin} /> },
    ...(isSuperAdmin ? {
      admin: { title: 'Admin Panel', icon: Settings, render: () => <AdminSection /> },
      bulkUpload: { title: 'Bulk Upload Guide', icon: Upload, render: () => <BulkUploadSection /> },
    } : {}),
    ...(isClinical ? {} : { troubleshooting: { title: 'Troubleshooting', icon: AlertCircle, render: () => <TroubleshootingSection isSuperAdmin={isSuperAdmin} /> } }),
  };
  const requested = searchParams.get('section');
  const [activeSection, setActiveSection] = useState(requested && sections[requested] ? requested : (isClinical ? 'workflow' : 'overview'));
  const backTo = CLINICAL_ROLES.includes(role) ? '/workflow' : '/dashboard';

  const SectionButton = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setActiveSection(id)}
      data-testid={`help-section-${id}`}
      className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${
        activeSection === id
          ? 'bg-blue-500 text-white shadow-md'
          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
    </button>
  );

  const ActiveIcon = sections[activeSection].icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6 print:bg-white print:p-0">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 print:hidden">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 min-w-0">
              <Button
                onClick={() => navigate(backTo)}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="help-back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4" />
                {backTo === '/workflow' ? 'Back to Workflow' : 'Back to Dashboard'}
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Help & Documentation</h1>
                <p className="text-sm text-gray-600">
                  {isSuperAdmin ? 'System Administrator Guide' : `${roleLabel(role)} Guide - ${hospitalName}`}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-3 print:hidden">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Documentation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(sections).map(([id, section]) => (
                  <SectionButton key={id} id={id} label={section.title} icon={section.icon} />
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="col-span-12 lg:col-span-9 print:col-span-12">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ActiveIcon className="w-6 h-6" />
                  {sections[activeSection].title}
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none" data-testid="help-section-content">
                {sections[activeSection].render()}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
